import React, { useState } from 'react';
import {
  CircularProgress,
  Container,
  Grid,
  Switch,
  Typography
} from '@mui/material';
import TopologyColumn from './topologyColumn';
import {
  getAmount,
  getHighestConnectionValue,
  JSONRecordData
} from '../Log/graph.util';
import { FormattedMessage } from 'react-intl';
import useIsMobile from '../../../hooks/useIsMobile';

interface TopologySodistoreGridProps {
  values: JSONRecordData;
  connected: boolean;
  loading: boolean;
}

function TopologySodistoreGrid(props: TopologySodistoreGridProps) {
  if (props.values === null && props.connected == true) {
    return null;
  }

  const highestConnectionValue =
    props.values != null ? getHighestConnectionValue(props.values) : 0;

  const [showValues, setShowValues] = useState(false);

  const handleSwitch = () => () => {
    setShowValues(!showValues);
  };

  const isMobile = window.innerWidth <= 1490;
  const isPhone = useIsMobile();

  const inv = props.values?.InverterRecord;
  const dcdc = props.values?.DcDc;
  const clusters = props.values?.ListOfBatteriesRecord ?? {};
  const clusterIds = Object.keys(clusters).sort(
    (a, b) => Number(a) - Number(b)
  );

  const acDcPower = Number(inv?.ActivePowerW ?? 0);
  const dcLinkPower = Number(dcdc?.Dc?.Link?.Power ?? 0);
  const dcLinkVoltage = Number(dcdc?.Dc?.Link?.Voltage ?? 0);
  const dcdcBatteryVoltage = Number(dcdc?.Dc?.Battery?.Voltage ?? 0);
  const dcdcBatteryPower = Number(dcdc?.Dc?.Battery?.Power ?? 0);

  return (
    <Container maxWidth="xl" style={{ backgroundColor: 'white' }}>
      <Grid container>
        {!props.connected && !props.loading && (
          <Container
            maxWidth="xl"
            sx={{
              display: 'flex',
              flexDirection: 'column',
              justifyContent: 'center',
              alignItems: 'center',
              height: '70vh'
            }}
          >
            <CircularProgress size={60} style={{ color: '#ffc04d' }} />
            <Typography
              variant="body2"
              style={{ color: 'black', fontWeight: 'bold' }}
              mt={2}
            >
              <FormattedMessage
                id="unableToCommunicate"
                defaultMessage="Unable to communicate with the installation"
              />
            </Typography>
            <Typography variant="body2" style={{ color: 'black' }}>
              <FormattedMessage
                id="pleaseWaitOrRefresh"
                defaultMessage="Please wait or refresh the page"
              />
            </Typography>
          </Container>
        )}

        {props.connected && (
          <>
            <Grid
              item
              xs={12}
              md={12}
              style={{
                marginTop: '10px',
                height: '20px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'right',
                justifyContent: 'right'
              }}
            >
              <div>
                <Typography sx={{ marginTop: '5px', marginRight: '20px' }}>
                  Display Values
                </Typography>
                <Switch
                  edge="start"
                  color="secondary"
                  onChange={handleSwitch()}
                  sx={{
                    '& .MuiSwitch-thumb': {
                      backgroundColor: 'orange'
                    },
                    marginLeft: '20px'
                  }}
                />
              </div>
            </Grid>

            <Grid
              item
              xs={12}
              md={12}
              style={{
                height: isMobile ? '550px' : '600px',
                display: 'flex',
                flexDirection: 'row',
                alignItems: 'center',
                // Phone: scroll from the left so nothing is clipped; desktop: centered.
                justifyContent: isPhone ? 'flex-start' : 'center',
                overflowX: isPhone ? 'auto' : 'visible',
                WebkitOverflowScrolling: 'touch'
              }}
            >
              {/* AC-DC */}
              <TopologyColumn
                centerBox={{
                  title: 'AC-DC',
                  data: inv
                    ? [{ value: acDcPower, unit: 'W' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: inv ? { value: acDcPower, unit: 'W' } : undefined,
                  amount: inv
                    ? getAmount(highestConnectionValue, acDcPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={true}
              />

              {/* DC Link */}
              <TopologyColumn
                centerBox={{
                  title: 'DC Link',
                  data: dcdc
                    ? [{ value: dcLinkVoltage, unit: 'V' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: dcdc
                    ? { value: dcLinkPower, unit: 'W' }
                    : undefined,
                  amount: dcdc
                    ? getAmount(highestConnectionValue, dcLinkPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />

              {/* DC-DC */}
              <TopologyColumn
                centerBox={{
                  title: 'DC-DC',
                  data: dcdc
                    ? [{ value: dcdcBatteryVoltage, unit: 'V' }]
                    : undefined,
                  connected: true
                }}
                centerConnection={{
                  orientation: 'horizontal',
                  data: dcdc
                    ? { value: dcdcBatteryPower, unit: 'W' }
                    : undefined,
                  amount: dcdc
                    ? getAmount(highestConnectionValue, dcdcBatteryPower)
                    : 0,
                  showValues: showValues
                }}
                isLast={false}
                isFirst={false}
              />

              {/* Battery clusters — one column per cluster, no chained lines */}
              {clusterIds.map((id) => {
                const c = clusters[id];
                return (
                  <TopologyColumn
                    key={`battery-cluster-${id}`}
                    centerBox={{
                      title: `Battery ${id}`,
                      data: c
                        ? [
                            { value: Number(c.Soc ?? 0), unit: '%' },
                            { value: Number(c.Voltage ?? 0), unit: 'V' },
                            { value: Number(c.Current ?? 0), unit: 'A' },
                            {
                              value: Number(c.TemperatureCell1 ?? 0),
                              unit: '°C'
                            }
                          ]
                        : undefined,
                      connected: true
                    }}
                    isLast={true}
                    isFirst={false}
                  />
                );
              })}
            </Grid>
          </>
        )}
      </Grid>
    </Container>
  );
}

export default TopologySodistoreGrid;
